<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import DenseTable from '@/components/ui/DenseTable.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Paginator from 'primevue/paginator'
import Tag from 'primevue/tag'
import { useAuthStore } from '@/stores/auth'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { useFiltrosEnUrl } from '@/composables/useFiltrosEnUrl'
import { customerApi, type Customer } from '@/services/customers'

/**
 * Directorio de clientes.
 *
 * En escritorio se usa `DenseTable` y en móvil una lista de cards. El nombre
 * comercial es lo que el comercial lee para reconocer al cliente, así que es la
 * columna que encabeza la lista y la que nunca se oculta; el resto de campos son
 * de consulta y se pueden plegar.
 *
 * `loading`, `error` e `items` se guardan por separado: con un solo booleano un
 * fallo de red se pintaría como "no hay clientes", que es un diagnóstico falso.
 */

const router = useRouter()
const authStore = useAuthStore()
const layout = useBreakpoint()

const isDesktop = computed(() => layout.value === 'desktop')
const canManage = computed(() => authStore.hasPermission('customers.manage'))

const items = ref<Customer[]>([])
const loading = ref(false)
const errorMessage = ref<string | null>(null)
const exporting = ref(false)
const search = ref('')
const page = ref(1)
const total = ref(0)
const perPage = 20

/*
| Los filtros viven en la URL
|--------------------------------------------------------------------------
|
| Filtrabas, abías un cliente, volvías, y el filtro había desaparecido. No era el botón de
| atrás: es que los filtros estaban en `ref`s de este componente, y un componente no
| sobrevive a que te vayas a otra vista. El filtro no tenía dónde quedarse; la URL sí
| aguanta.
*/
const filtros = useFiltrosEnUrl({
  busqueda: { ref: search, texto: true },
  pagina: { ref: page, numero: true },
})

const rows = computed(() =>
  items.value.map((item) => ({
    ...item,
    // Las etiquetas se calculan aquí y no con un `template` por columna:
    // `DenseTable` imprime `row[field]`, así que el formateo se resuelve una vez
    // por fila.
    document_label: item.document ?? '—',
    phone_label: item.phone ?? '—',
    email_label: item.email ?? '—',
    city_label: item.city ?? '—',
    source_label: item.source ?? '—',
    actions: canManage.value
      ? [
          { label: 'Editar', value: 'edit', icon: 'pi pi-pencil' },
          { label: 'Borrar', value: 'delete', icon: 'pi pi-trash', severity: 'danger' },
        ]
      : [{ label: 'Ver detalle', value: 'view', icon: 'pi pi-eye' }],
  })),
)

/**
 * Columnas de la tabla densa. Los campos apuntan a las etiquetas ya
 * normalizadas de `rows`, donde un `null` se ha convertido en "—": sin eso la
 * celda saldría vacía y no se distinguiría de un dato vacío de verdad.
 */
const visibleColumns = [
  { field: 'name', header: 'Cliente' },
  { field: 'document_label', header: 'Documento' },
  { field: 'phone_label', header: 'Teléfono', hideOnMobile: true },
  { field: 'email_label', header: 'Email', hideOnMobile: true },
  { field: 'city_label', header: 'Ciudad', hideOnMobile: true },
  { field: 'source_label', header: 'Origen', hideOnMobile: true },
]

const hasFilters = computed(() => search.value.trim() !== '')
const isEmpty = computed(() => !loading.value && errorMessage.value === null && items.value.length === 0)

const rangeLabel = computed(() => {
  if (total.value === 0) return ''
  const first = (page.value - 1) * perPage + 1
  const last = Math.min(page.value * perPage, total.value)
  return `${first}–${last} de ${total.value}`
})

function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar el directorio de clientes. Revisa tu conexión e inténtalo de nuevo.'
}

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await customerApi.list({
      search: search.value.trim() || undefined,
      page: page.value,
      per_page: perPage,
    })

    items.value = data.data
    total.value = data.meta.total
  } catch (cause: unknown) {
    items.value = []
    total.value = 0
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

let debounceHandle: ReturnType<typeof setTimeout> | null = null

watch(search, () => {
  page.value = 1
  if (debounceHandle !== null) clearTimeout(debounceHandle)
  // Sin debounce, cada tecla dispara una petición y las respuestas pueden
  // llegar desordenadas, pintando resultados de un término anterior.
  debounceHandle = setTimeout(() => {
    void load()
  }, 300)
})

watch(page, () => {
  void load()
})

onMounted(() => {
  void load()
})

/** `DenseTable` tipa las filas como `unknown`; aquí seestrechan. */
function handleAction(row: unknown, action: string): void {
  const customer = row as Customer

  if (action === 'view' || action === 'edit') {
    void router.push(filtros.conQuery({ name: 'customers.show', params: { id: String(customer.id) } }))
    return
  }

  if (action === 'delete') {
    void router.push(
      filtros.conQuery({
        name: 'customers.show',
        params: { id: String(customer.id) },
        query: { action: 'delete' },
      }),
    )
  }
}

function onPageChange(event: { page: number }): void {
  page.value = event.page + 1
}

async function exportCustomers(): Promise<void> {
  exporting.value = true
  try {
    await customerApi.export({ search: search.value.trim() || undefined })
  } catch (cause: unknown) {
    const maybeAxios = cause as { message?: string }
    errorMessage.value = maybeAxios.message ?? 'No se pudo exportar el directorio.'
  } finally {
    exporting.value = false
  }
}

function clearFilters(): void {
  filtros.limpiar()
  page.value = 1
  void load()
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Clientes
        </h1>
        <p class="mt-1 text-surface-500 dark:text-surface-400">
          Directorio comercial reutilizable en las cotizaciones
        </p>
      </div>
      <div class="flex gap-2">
        <Button
          label="Exportar"
          icon="pi pi-download"
          severity="secondary"
          :loading="exporting"
          @click="exportCustomers"
        />
        <Button
          v-if="canManage"
          label="Nuevo"
          icon="pi pi-plus"
          @click="router.push({ name: 'customers.create' })"
        />
      </div>
    </header>

    <AppCard>
      <template #header>
        <form
          class="flex flex-col gap-3 sm:flex-row"
          role="search"
          @submit.prevent
        >
          <InputText
            v-model="search"
            placeholder="Buscar por nombre, documento o email"
            aria-label="Buscar en el directorio de clientes"
            class="w-full"
          />
        </form>
      </template>

      <!-- Estado de error: separado del vacío para no mentir sobre la causa. -->
      <div
        v-if="errorMessage"
        class="flex flex-col items-center gap-3 py-12 text-center"
        role="alert"
      >
        <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          No se pudo cargar el directorio
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{ errorMessage }}
        </p>
        <Button
          label="Reintentar"
          icon="pi pi-refresh"
          severity="secondary"
          @click="load"
        />
      </div>

      <!-- Estado vacío -->
      <div
        v-else-if="isEmpty"
        class="flex flex-col items-center gap-3 py-12 text-center"
      >
        <i class="pi pi-inbox text-4xl text-surface-300 dark:text-surface-600" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          {{ hasFilters ? 'Sin resultados' : 'Todavía no hay clientes' }}
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{
            hasFilters
              ? 'Prueba con otro nombre, documento o email.'
              : 'Da de alta el primer cliente para poder cotizarle.'
          }}
        </p>
        <Button
          v-if="hasFilters"
          label="Quitar búsqueda"
          severity="secondary"
          outlined
          @click="clearFilters"
        />
        <Button
          v-else-if="canManage"
          label="Crear el primero"
          icon="pi pi-plus"
          @click="router.push({ name: 'customers.create' })"
        />
      </div>

      <!-- Estado con datos -->
      <template v-else>
        <DenseTable
          v-if="isDesktop"
          :columns="visibleColumns"
          :rows="rows"
          :loading="loading"
          @row-click="handleAction($event, 'view')"
          @action="handleAction"
        />

        <ul
          v-else
          class="divide-y divide-surface-200 dark:divide-surface-700"
          data-testid="customers-mobile-list"
        >
          <li
            v-for="customer in items"
            :key="customer.id"
            class="py-3"
          >
            <div class="flex items-start justify-between gap-3">
              <p class="min-w-0 flex-1 truncate font-medium text-surface-900 dark:text-surface-100">
                {{ customer.name }}
              </p>
              <Tag
                v-if="customer.source"
                :value="customer.source"
                severity="secondary"
              />
            </div>

            <dl class="mt-2 space-y-1 text-sm">
              <div
                v-if="customer.document"
                class="flex gap-2"
              >
                <dt class="text-surface-500 dark:text-surface-400">
                  Documento
                </dt>
                <dd class="font-mono text-surface-900 dark:text-surface-100">
                  {{ customer.document }}
                </dd>
              </div>
              <div
                v-if="customer.phone"
                class="flex gap-2"
              >
                <dt class="text-surface-500 dark:text-surface-400">
                  Teléfono
                </dt>
                <dd class="text-surface-900 dark:text-surface-100">
                  {{ customer.phone }}
                </dd>
              </div>
              <div
                v-if="customer.email"
                class="flex gap-2"
              >
                <dt class="text-surface-500 dark:text-surface-400">
                  Email
                </dt>
                <dd class="min-w-0 truncate text-surface-900 dark:text-surface-100">
                  {{ customer.email }}
                </dd>
              </div>
              <div
                v-if="customer.city"
                class="flex gap-2"
              >
                <dt class="text-surface-500 dark:text-surface-400">
                  Ciudad
                </dt>
                <dd class="text-surface-900 dark:text-surface-100">
                  {{ customer.city }}
                </dd>
              </div>
            </dl>

            <div class="mt-3 flex flex-wrap gap-2">
              <Button
                size="small"
                severity="secondary"
                outlined
                label="Ver"
                icon="pi pi-eye"
                @click="router.push({ name: 'customers.show', params: { id: String(customer.id) } })"
              />
              <Button
                v-if="canManage"
                size="small"
                severity="secondary"
                outlined
                label="Editar"
                icon="pi pi-pencil"
                @click="router.push({ name: 'customers.show', params: { id: String(customer.id) }, query: { action: 'edit' } })"
              />
            </div>
          </li>
        </ul>

        <div
          v-if="total > perPage"
          class="flex flex-col items-center gap-3 border-t border-surface-200 pt-4 sm:flex-row sm:justify-between dark:border-surface-700"
        >
          <p class="text-sm text-surface-500 dark:text-surface-400">
            {{ rangeLabel }}
          </p>
          <Paginator
            :first="(page - 1) * perPage"
            :rows="perPage"
            :total-records="total"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            @page="onPageChange"
          />
        </div>
      </template>
    </AppCard>
  </div>
</template>
